import './login.css';
import React, {Component} from "react";
import AuthenticationService from "../services/authenticationService";
import {Navigate} from "react-router-dom";
import {ToastErrorLevel} from "../../App";
import {withRouter} from "./withRouter";
import LanguageSelector from "./languageSelector";
import i18nService from "../../i18n/i18nService";

interface LoginState {
    email: string,
    password: string,
    navigation?: string,
    showForgotPassword: boolean,
    forgotPasswordEmail: string,
    forgotPasswordMessage?: string,
    isLoginSubmitting: boolean,
    isForgotSubmitting: boolean,
    locale: string,
}

interface LoginProps {
    toastGenerator: (body: string, level: ToastErrorLevel) => any,
    onLogin: any,
}

const UNDER_MAINTENANCE = false;

class Login extends Component<LoginProps, LoginState> {

    private unsubscribe?: () => void;

    constructor(props: LoginProps) {
        super(props, {})
        this.state = {
            email: "", 
            password: "",
            showForgotPassword: false,
            forgotPasswordEmail: "",
            isLoginSubmitting: false,
            isForgotSubmitting: false,
            locale: i18nService.getLocale(),
        };
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleEmailUpdate = this.handleEmailUpdate.bind(this);
        this.handlePassUpdate = this.handlePassUpdate.bind(this);
        this.handleForgotPassword = this.handleForgotPassword.bind(this);
        this.handleForgotPasswordSubmit = this.handleForgotPasswordSubmit.bind(this);
        this.handleLocaleChange = this.handleLocaleChange.bind(this);
    }

    componentWillUnmount() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    }

    handleLocaleChange(locale: string) {
        this.setState({ locale });
    }

    async componentDidMount() {
        // Subscribe to locale changes
        this.unsubscribe = i18nService.subscribe(() => {
            this.setState({ locale: i18nService.getLocale() });
        });

        // Check for returnTo parameter
        // @ts-ignore
        const searchParams = new URLSearchParams(this.props.location?.search || '');
        const returnTo = searchParams.get('returnTo') || '/dashboard';

        // if already logged in, redirect to returnTo or home page
        await AuthenticationService.verifyLoggedIn(async loggedInUser => {
                // if we are already logged in, we should redirect to the returnTo page
                console.log("Already logged in. Skipping login");
                this.setState({
                    navigation: returnTo,
                });
            }, async failure => {
                this.props.toastGenerator(failure, ToastErrorLevel.ERROR);
            },
            async redirect => {
                // we do not actually redirect on failure in this case as we are already on the login page
            })
    }

    async handleSubmit(event: any) {
        // Prevent page reload
        event.preventDefault();
        if (this.state.isLoginSubmitting) {
            return;
        }
        this.setState({isLoginSubmitting: true});
        
        // Get returnTo parameter
        // @ts-ignore
        const searchParams = new URLSearchParams(this.props.location?.search || '');
        const returnTo = searchParams.get('returnTo') || '/dashboard';

        await AuthenticationService.attemptLogin(this.state.email, this.state.password,
            async () => {
                await this.props.onLogin();
                this.setState({
                    navigation: returnTo,
                    isLoginSubmitting: false,
                })
            },
            async (error) => {
                this.setState({isLoginSubmitting: false});
                this.props.toastGenerator(error, ToastErrorLevel.ERROR);
            })
    }

    handleEmailUpdate(event: any) {
        this.setState({
            ...this.state,
            email: event.target.value
        })
    }

    handlePassUpdate(event: any) {
        this.setState({
            ...this.state,
            password: event.target.value
        })
    }

    async handleForgotPassword() {
        this.setState({showForgotPassword: true});
    }

    async handleForgotPasswordSubmit(event: any) {
        event.preventDefault();
        if (this.state.isForgotSubmitting) {
            return;
        }
        if (!this.state.forgotPasswordEmail.trim()) {
            this.setState({forgotPasswordMessage: i18nService.t('errors.emailRequired')});
            return;
        }
        this.setState({isForgotSubmitting: true});
        await AuthenticationService.forgotPassword(
            this.state.forgotPasswordEmail.trim(),
            async () => {
                this.setState({
                    forgotPasswordMessage: i18nService.t('auth.passwordResetSent', 'If the email exists, a password reset link has been sent. Please check your email.'),
                    isForgotSubmitting: false,
                });
            },
            async (error) => {
                this.setState({forgotPasswordMessage: error, isForgotSubmitting: false});
            }
        );
    }

    render() {
        return (
            <div>
                {this.state.navigation && <Navigate to={this.state.navigation}/>}
                <div className="Login-Wrapper">
                    <div className="Login-LanguageSelector">
                        <LanguageSelector ephemeral={true} onLocaleChange={this.handleLocaleChange} />
                    </div>
                    <div className="Login-form">
                        { UNDER_MAINTENANCE && <div className={"Login-Maintenance"}>
                            <h2>{i18nService.t('auth.loginUnavailable', 'Login Unavailable')}</h2>
                            <p>{i18nService.t('auth.maintenanceMessage', 'The login system is currently under maintenance. Please try again later.')}</p>
                        </div>}
                        { !UNDER_MAINTENANCE && <form>
                            <div className="Login-input-container">
                                <input type="text" name="email" required placeholder={i18nService.t('auth.email')}
                                       onChange={this.handleEmailUpdate} value={this.state.email}/>
                            </div>
                            <div className="Login-input-container">
                                <input type="password" name="pass" required placeholder={i18nService.t('auth.password')}
                                       onChange={this.handlePassUpdate} value={this.state.password}/>
                            </div>
                            <div className="Login-button-container">
                                <button onClick={this.handleSubmit} disabled={this.state.isLoginSubmitting}>
                                    {this.state.isLoginSubmitting ? i18nService.t('auth.loggingIn', 'Logging in...') : i18nService.t('auth.login')}
                                </button>
                            </div>
                            <div className="Login-forgot-password">
                                <button type="button" className="Login-ForgotPasswordLink" onClick={(e) => {
                                    e.preventDefault();
                                    this.setState({showForgotPassword: true});
                                }}>
                                    {i18nService.t('auth.forgotPassword')}
                                </button>
                            </div>
                        </form> }
                        {this.state.showForgotPassword && (
                            <div className="Login-forgot-password-form">
                                <h3>{i18nService.t('auth.resetPassword')}</h3>
                                <p>{i18nService.t('auth.resetPasswordDescription', 'Enter your email address and we\'ll send you a link to reset your password.')}</p>
                                <div className="Login-input-container">
                                    <input 
                                        type="email" 
                                        placeholder={i18nService.t('auth.email')} 
                                        value={this.state.forgotPasswordEmail}
                                        onChange={(e) => this.setState({forgotPasswordEmail: e.target.value})}
                                    />
                                </div>
                                {this.state.forgotPasswordMessage && (
                                    <div className={`Login-message ${this.state.forgotPasswordMessage.includes("success") || this.state.forgotPasswordMessage.includes(i18nService.t('auth.passwordResetSent', '')) ? "success" : "error"}`}>
                                        {this.state.forgotPasswordMessage}
                                    </div>
                                )}
                                <div className="Login-button-container">
                                    <button onClick={this.handleForgotPasswordSubmit} disabled={this.state.isForgotSubmitting}>
                                        {this.state.isForgotSubmitting ? i18nService.t('auth.sending', 'Sending...') : i18nService.t('auth.sendResetLink', 'Send Reset Link')}
                                    </button>
                                    <button 
                                        className="Login-cancel-button"
                                        disabled={this.state.isForgotSubmitting}
                                        onClick={() => this.setState({showForgotPassword: false, forgotPasswordEmail: "", forgotPasswordMessage: undefined, isForgotSubmitting: false})}
                                    >
                                        {i18nService.t('common.cancel')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>);
    }
}

export default withRouter(Login);